import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { invoiceService } from '../services/invoiceService';
import { Invoice } from '../types';
import { formatCalendarDate } from '../utils/datetime';

const PAYMENTS_LOGO = '/InvoicesImages/UptimioInvoices.svg';
const SIGNATURE_IMG = '/UptimioSignature.png';

const a4Styles: React.CSSProperties = {
  width: '210mm',
  minHeight: '297mm',
  margin: '0 auto',
  background: '#fff',
  padding: '16mm',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column'
};

const zipCountryLine = (zip?: string, country?: string) =>
  [zip, country].filter(Boolean).join(', ');

const InvoiceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!id) {
        setError('Missing invoice id');
        return;
      }
      setLoading(true);
      try {
        const data = await invoiceService.getPublicInvoice(id);
        setInvoice(data);
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const prevTitle = document.title;
    const before = () => { document.title = ' '; };
    const after = () => { document.title = prevTitle; };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      document.title = prevTitle;
    };
  }, []);

  const total = invoice?.amount || 0;
  const correspondents = invoice
    ? ([1, 2, 3, 4, 5] as const)
      .map((n) => ({
        bank: (invoice as any)[`issuerCorrespondentBank${n}`] as string | undefined,
        swift: (invoice as any)[`issuerCorrespondentSwift${n}`] as string | undefined,
        country: (invoice as any)[`issuerCorrespondentCountry${n}`] as string | undefined
      }))
      .filter((item) => item.bank)
    : [];

  return (
    <div className="invoice-view" style={{ padding: 24 }}>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; overflow: visible !important; }
          .no-print { display: none !important; }
          .invoice-view { padding: 0 !important; }
          .p-card, .p-card .p-card-body, .p-card .p-card-content { border: 0 !important; box-shadow: none !important; padding: 0 !important; margin: 0 !important; background: transparent !important; }
          .invoice-paper {
            width: 100% !important;
            min-height: 0 !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: 0 !important;
            font-size: 11.5px;
            line-height: 1.35;
          }
          .invoice-logo { height: 42px !important; margin-bottom: 6px !important; }
          .invoice-header { font-size: 13px !important; line-height: 1.4 !important; }
          .invoice-table thead th, .invoice-table tbody td, .invoice-table tfoot td { padding: 6px 8px !important; }
          .payment-info { margin-top: 8px !important; padding: 8px 10px !important; }
          .payment-info h4, .correspondent-banks h5, .payment-instructions h5 { font-size: 11px !important; margin-bottom: 4px !important; }
          .payment-label, .bank-details, .instructions-text { font-size: 9.5px !important; }
          .payment-value, .bank-name { font-size: 10.5px !important; }
          .payment-details { gap: 6px 12px !important; }
          .correspondent-banks, .payment-instructions { margin-top: 8px !important; }
          .correspondent-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 4px 16px !important;
          }
          .correspondent-bank-compact { margin-bottom: 0 !important; padding-bottom: 4px !important; }
          .instructions-text { padding: 5px 7px !important; break-inside: avoid; page-break-inside: avoid; }
          .invoice-signatures { position: static !important; margin-top: 16px !important; page-break-inside: avoid; break-inside: avoid; }
          .invoice-signatures img { height: 40px !important; }
        }
        .invoice-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
        .invoice-table thead th { background: #f9fafb; color: #111827; font-weight: 600; font-size: 12.5px; padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
        .invoice-table tbody td { padding: 10px 12px; vertical-align: top; font-size: 12.5px; }
        .invoice-table tbody tr:nth-child(even) { background: #fcfcfd; }
        .invoice-table tfoot td { padding: 12px; background: #fafafa; font-size: 13px; border-top: 1px solid #e5e7eb; }
        .col-desc { width: 55%; }
        .col-qty { width: 10%; text-align: right; }
        .col-unit { width: 15%; text-align: right; }
        .col-amount { width: 20%; text-align: right; font-weight: 600; }
        .invoice-header { font-size: 13.5px; line-height: 1.45; }
        .invoice-header .issuer-name, .invoice-header .client-name { font-size: 15px; font-weight: 700; }
        .invoice-number { color: #6b7280; font-weight: 700; margin-bottom: 6px; font-size: 14px; }
        .payment-info { margin-top: 12px; padding: 8px 10px; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px; }
        .payment-info h4 { margin: 0 0 6px 0; font-size: 12px; font-weight: 600; color: #374151; }
        .payment-bank { margin-bottom: 8px; }
        .payment-details { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 12px; }
        .payment-item { display: flex; flex-direction: column; }
        .payment-label { font-size: 9.5px; font-weight: 600; color: #6b7280; margin-bottom: 1px; }
        .payment-value { font-size: 11px; font-weight: 500; color: #111827; overflow-wrap: anywhere; }
        .correspondent-banks { margin-top: 8px; }
        .correspondent-banks h5 { margin: 0 0 6px 0; font-size: 12px; font-weight: 600; color: #374151; }
        .correspondent-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 20px;
          width: 100%;
        }
        .correspondent-bank-compact { padding-bottom: 4px; }
        .bank-name { font-size: 11px; font-weight: 600; color: #111827; margin-bottom: 1px; }
        .bank-details { font-size: 9.5px; color: #6b7280; }
        .bank-details .swift { font-weight: 500; }
        .bank-details .country { color: #9ca3af; }
        .payment-instructions { margin-top: 8px; }
        .payment-instructions h5 { margin: 0 0 4px 0; font-size: 12px; font-weight: 600; color: #374151; }
        .instructions-text { white-space: pre-line; font-size: 10px; line-height: 1.35; background: #f8f9fa; padding: 6px 8px; border-radius: 4px; border: 1px solid #e9ecef; }
        .invoice-body { flex: 1 1 auto; }
        .invoice-signatures { margin-top: auto; padding-top: 18px; }
        .signature-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; }
        .signature-block { display: flex; flex-direction: column; align-items: center; text-align: center; width: 70mm; }
        .signature-line { width: 100%; border-bottom: 1px solid #374151; margin-top: 8px; }
        .signature-caption { font-size: 11px; color: #6b7280; margin-top: 4px; }
        @media screen and (max-width: 640px) {
          .payment-details { grid-template-columns: 1fr; gap: 8px; }
        }
      `}</style>
      <Card>
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Invoice</h2>
          <Button label="Print / Save PDF" icon="pi pi-print" onClick={() => window.print()} />
        </div>
        {loading && <p>Loading...</p>}
        {error && <Message severity="error" text={error} />}
        {invoice && (
          <div className="invoice-paper" style={{ marginTop: 16, ...a4Styles }}>
            <div className="invoice-body">
            <div className="invoice-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <img className="invoice-logo" src={PAYMENTS_LOGO} alt="Uptimio" style={{ height: 46, width: 'auto', maxWidth: 260, marginBottom: 8 }} />
                {invoice.invoiceNumberStr && <div className="invoice-number">{invoice.invoiceNumberStr}</div>}
                <div className="issuer-name" style={{ marginTop: 4 }}>{invoice.issuerName}</div>
                {invoice.issuerAddress && <div>{invoice.issuerAddress}</div>}
                {zipCountryLine(invoice.issuerZipCode, invoice.issuerCountry) && (
                  <div>{zipCountryLine(invoice.issuerZipCode, invoice.issuerCountry)}</div>
                )}
                <div>{invoice.issuerPhone || '+38162460696'}</div>
                <div>{invoice.issuerEmail || 'invoices@uptimio.com'}</div>
              </div>
              <div style={{ textAlign: 'right', marginTop: 28 }}>
                <div style={{ fontWeight: 600 }}>Bill to</div>
                <div className="client-name">{invoice.clientName}</div>
                <div>{invoice.clientEmail}</div>
                {invoice.clientCompanyName && <div style={{ fontWeight: 600 }}>{invoice.clientCompanyName}</div>}
                {invoice.clientAddress && <div>{invoice.clientAddress}</div>}
                {zipCountryLine(invoice.clientZipCode, invoice.clientCountry) && (
                  <div>{zipCountryLine(invoice.clientZipCode, invoice.clientCountry)}</div>
                )}
                {invoice.clientPhone && <div>{invoice.clientPhone}</div>}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th className="col-desc" style={{ textAlign: 'left' }}>Description</th>
                    <th className="col-qty">Qty/Hrs</th>
                    <th className="col-unit">Unit price</th>
                    <th className="col-amount">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((it, idx) => (
                    <tr key={idx}>
                      <td className="col-desc">
                        <div style={{ whiteSpace: 'pre-wrap' }}>{it.description}</div>
                      </td>
                      <td className="col-qty">{it.quantity}</td>
                      <td className="col-unit">{it.unitPrice.toLocaleString('en-US', { style: 'currency', currency: invoice.currency || 'USD' })}</td>
                      <td className="col-amount">{(it.quantity * it.unitPrice).toLocaleString('en-US', { style: 'currency', currency: invoice.currency || 'USD' })}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'right' }}>Total</td>
                    <td className="col-amount">{total.toLocaleString('en-US', { style: 'currency', currency: invoice.currency || 'USD' })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600 }}>Due date: <span style={{ fontWeight: 700 }}>{formatCalendarDate(invoice.dueDate)}</span></div>
            </div>

            {(invoice.issuerSwiftCode || invoice.issuerIban || invoice.issuerCardNumber || invoice.issuerBankName) && (
              <div className="payment-info">
                <h4>Payment Information</h4>
                {invoice.issuerBankName && (
                  <div className="payment-item payment-bank">
                    <div className="payment-label">Beneficiary Bank</div>
                    <div className="payment-value">{invoice.issuerBankName}</div>
                  </div>
                )}
                <div className="payment-details">
                  {invoice.issuerSwiftCode && (
                    <div className="payment-item">
                      <div className="payment-label">SWIFT Code</div>
                      <div className="payment-value">{invoice.issuerSwiftCode}</div>
                    </div>
                  )}
                  {invoice.issuerIban && (
                    <div className="payment-item">
                      <div className="payment-label">IBAN</div>
                      <div className="payment-value">{invoice.issuerIban}</div>
                    </div>
                  )}
                  {invoice.issuerCardNumber && (
                    <div className="payment-item">
                      <div className="payment-label">Card Number</div>
                      <div className="payment-value">{invoice.issuerCardNumber}</div>
                    </div>
                  )}
                </div>

                {correspondents.length > 0 && (
                  <div className="correspondent-banks">
                    <h5>Correspondents:</h5>
                    <div className="correspondent-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', width: '100%' }}>
                      {correspondents.map((item) => (
                        <div className="correspondent-bank-compact" key={item.bank}>
                          <div className="bank-name"><strong>{item.bank}</strong></div>
                          {(item.swift || item.country) && (
                            <div className="bank-details">
                              {item.swift && <span className="swift">SWIFT/BIC: {item.swift}</span>}
                              {item.swift && item.country && (
                                <span className="country"> | Country: {item.country}</span>
                              )}
                              {!item.swift && item.country && (
                                <span className="country">Country: {item.country}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {invoice.issuerPaymentInstructions && (
                  <div className="payment-instructions">
                    <h5>Payment Instructions</h5>
                    <div className="instructions-text">{invoice.issuerPaymentInstructions}</div>
                  </div>
                )}
              </div>
            )}

            {invoice.paymentMethod && (
              <div style={{ marginTop: 8 }}>
                <span style={{ fontWeight: 600 }}>Payment method: </span>
                <span>{invoice.paymentMethod}</span>
              </div>
            )}

            {invoice.notes && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600 }}>Notes</div>
                <div>{invoice.notes}</div>
              </div>
            )}
            </div>

            <div className="invoice-signatures">
              <div className="signature-row">
                <div className="signature-block">
                  <img src={SIGNATURE_IMG} alt="signature" style={{ height: 56 }} />
                  <div className="signature-line" />
                  <div className="signature-caption">(Issuer Signature)</div>
                </div>
                <div className="signature-block">
                  <div className="signature-line" />
                  <div className="signature-caption">(Client Signature)</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InvoiceView;
